import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';

/**
 * WorkflowVerificationPanel
 *
 * Admin tool to verify workflow setup:
 * - Workflow hierarchy (workflows → sub-workflows → steps)
 * - MVV data completeness
 * - Prerequisites
 * - Date calculation configuration
 */
const WorkflowVerificationPanel = () => {
  const [workflows, setWorkflows] = useState([]);
  const [mvvAudit, setMvvAudit] = useState([]);
  const [missingMvv, setMissingMvv] = useState([]);
  const [prerequisites, setPrerequisites] = useState([]);
  const [intensityDistribution, setIntensityDistribution] = useState([]);
  const [actionFieldsAudit, setActionFieldsAudit] = useState([]);
  const [workflowCompleteness, setWorkflowCompleteness] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('hierarchy');

  useEffect(() => {
    loadVerificationData();
  }, []);

  const loadVerificationData = async () => {
    setLoading(true);
    try {
      // Query 1: Workflow hierarchy
      // ORDERING: workflow_display_order → workflow_key → display_order → sub_workflow_key
      // This ensures: workflows ordered by display_order, tasks by display_order, sub-workflows alphabetically
      const { data: hierarchyData } = await supabase
        .from('milestone_templates')
        .select('workflow_key, workflow_display_order, sub_workflow_key, title, display_order, is_foundation_template, is_college_specific, min_viable_version, optimal_version, mvv_time_required, optimal_time_required, importance_level, timing_sensitivity, intensity_level, anchor_id, offset_days, action_label, action_route, unlock_description')
        .not('workflow_key', 'is', null)
        .eq('is_active', true)
        .order('workflow_display_order', { nullsLast: true })
        .order('workflow_key')
        .order('display_order')
        .order('sub_workflow_key');

      // Organize into workflow structure
      const workflowMap = {};
      hierarchyData.forEach(template => {
        if (!workflowMap[template.workflow_key]) {
          workflowMap[template.workflow_key] = {
            key: template.workflow_key,
            displayOrder: template.workflow_display_order || 999, // Use 999 for any workflows without display_order
            subWorkflows: {}
          };
        }

        const subKey = template.sub_workflow_key || 'ungrouped';
        if (!workflowMap[template.workflow_key].subWorkflows[subKey]) {
          workflowMap[template.workflow_key].subWorkflows[subKey] = {
            key: subKey,
            steps: []
          };
        }

        workflowMap[template.workflow_key].subWorkflows[subKey].steps.push(template);
      });

      // Convert to array and ensure it's sorted by displayOrder (already sorted from query, but ensure consistency)
      const sortedWorkflows = Object.values(workflowMap).sort((a, b) => a.displayOrder - b.displayOrder);
      setWorkflows(sortedWorkflows);

      // Calculate MVV audit
      const mvvStats = {};
      hierarchyData.forEach(t => {
        if (!mvvStats[t.workflow_key]) {
          mvvStats[t.workflow_key] = { total: 0, complete: 0, partial: 0, missing: 0 };
        }
        mvvStats[t.workflow_key].total++;
        if (t.min_viable_version && t.optimal_version) {
          mvvStats[t.workflow_key].complete++;
        } else if (t.min_viable_version || t.optimal_version) {
          mvvStats[t.workflow_key].partial++;
        } else {
          mvvStats[t.workflow_key].missing++;
        }
      });
      setMvvAudit(Object.entries(mvvStats).map(([key, stats]) => ({ workflow_key: key, ...stats })));

      // Find tasks missing MVV
      const missing = hierarchyData.filter(t =>
        !t.min_viable_version && !t.optimal_version &&
        !t.title.toLowerCase().includes('submit') &&
        !t.title.toLowerCase().includes('verify') &&
        !t.title.toLowerCase().includes('send')
      );
      setMissingMvv(missing);

      // Find prerequisites
      const prereqs = hierarchyData.filter(t => t.is_foundation_template);
      setPrerequisites(prereqs);

      // Intensity level distribution
      const intensityStats = {};
      hierarchyData.forEach(t => {
        const level = t.intensity_level || 'none';
        if (!intensityStats[level]) {
          intensityStats[level] = { count: 0, workflows: new Set() };
        }
        intensityStats[level].count++;
        intensityStats[level].workflows.add(t.workflow_key);
      });
      const totalTemplates = hierarchyData.length;
      setIntensityDistribution(
        Object.entries(intensityStats).map(([level, data]) => ({
          intensity_level: level,
          count: data.count,
          workflows: Array.from(data.workflows),
          percentage: Math.round((data.count / totalTemplates) * 100)
        }))
      );

      // Action fields audit
      const actionStats = {};
      hierarchyData.forEach(t => {
        if (!actionStats[t.workflow_key]) {
          actionStats[t.workflow_key] = { total: 0, hasActionLabel: 0, hasActionRoute: 0 };
        }
        actionStats[t.workflow_key].total++;
        if (t.action_label) actionStats[t.workflow_key].hasActionLabel++;
        if (t.action_route) actionStats[t.workflow_key].hasActionRoute++;
      });
      setActionFieldsAudit(
        Object.entries(actionStats).map(([key, stats]) => ({
          workflow_key: key,
          total: stats.total,
          hasActionLabel: stats.hasActionLabel,
          hasActionRoute: stats.hasActionRoute,
          pctWithActions: Math.round((stats.hasActionLabel / stats.total) * 100)
        }))
      );

      // Workflow completeness check
      const expectedWorkflows = [
        'build_strategic_profile',
        'build_college_list',
        'secure_recommendations',
        'write_personal_statement',
        'setup_application_accounts',
        'research_scholarships',
        'complete_college_app',
        'compare_financial_aid',
        'respond_to_waitlist',
        'prepare_for_enrollment'
      ];
      const actualWorkflows = new Set(hierarchyData.map(t => t.workflow_key));
      setWorkflowCompleteness(
        expectedWorkflows.map(key => ({
          workflow_key: key,
          exists: actualWorkflows.has(key),
          stepCount: hierarchyData.filter(t => t.workflow_key === key).length
        }))
      );

    } catch (error) {
      console.error('Error loading verification data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getWorkflowTitle = (key) => {
    const titles = {
      'build_strategic_profile': 'Build Your Strategic Profile',
      'build_college_list': 'Build Your College List',
      'secure_recommendations': 'Secure Strong Recommendations',
      'write_personal_statement': 'Write Your Personal Statement',
      'setup_application_accounts': 'Set Up Application Accounts',
      'research_scholarships': 'Research and Pursue Scholarships',
      'complete_college_application': 'Complete [College] Application',
      'make_final_decision': 'Make Final Decision',
      'prepare_for_enrollment': 'Prepare for Enrollment'
    };
    return titles[key] || key;
  };

  const getSubWorkflowTitle = (key) => {
    if (!key || key === 'ungrouped') return 'Ungrouped';
    return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getMvvStatus = (step) => {
    if (step.min_viable_version && step.optimal_version) return { label: '✅ Complete', color: '#10b981' };
    if (step.min_viable_version) return { label: '⚠️ MVV Only', color: '#f59e0b' };
    if (step.optimal_version) return { label: '⚠️ Optimal Only', color: '#f59e0b' };
    return { label: '❌ Missing', color: '#ef4444' };
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div>Loading workflow verification data...</div>
      </div>
    );
  }

  return (
    <div style={{
      fontFamily: '"Reddit Sans", system-ui, sans-serif',
      padding: '2rem',
      maxWidth: '1400px',
      margin: '0 auto'
    }}>
      <h1 style={{ fontSize: '2rem', fontWeight: 700, marginBottom: '0.5rem' }}>
        Workflow Setup Verification
      </h1>
      <p style={{ color: '#64748b', marginBottom: '2rem' }}>
        Comprehensive audit of workflow configuration, MVV data, prerequisites, and date calculation
      </p>

      {/* Tab Navigation */}
      <div style={{ borderBottom: '2px solid #e2e8f0', marginBottom: '2rem', display: 'flex', flexWrap: 'wrap' }}>
        {['hierarchy', 'mvv', 'prerequisites', 'dates', 'intensity', 'actions', 'completeness'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === tab ? '2px solid #26a69a' : '2px solid transparent',
              marginBottom: '-2px',
              cursor: 'pointer',
              fontWeight: activeTab === tab ? 600 : 400,
              color: activeTab === tab ? '#0f172a' : '#64748b',
              fontSize: '0.875rem'
            }}
          >
            {tab === 'hierarchy' && '📊 Hierarchy'}
            {tab === 'mvv' && '✅ MVV Audit'}
            {tab === 'prerequisites' && '🔒 Prerequisites'}
            {tab === 'dates' && '📅 Date Config'}
            {tab === 'intensity' && '🔥 Intensity Levels'}
            {tab === 'actions' && '🔗 Action Buttons'}
            {tab === 'completeness' && '✓ Completeness'}
          </button>
        ))}
      </div>

      {/* Hierarchy Tab */}
      {activeTab === 'hierarchy' && (
        <div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: '1rem' }}>
            Complete Workflow Hierarchy
          </h2>
          {workflows.map(workflow => (
            <div key={workflow.key} style={{
              border: '2px solid #e2e8f0',
              borderRadius: '8px',
              marginBottom: '1.5rem',
              overflow: 'hidden'
            }}>
              {/* Workflow Header */}
              <div style={{
                background: '#f8fafc',
                padding: '1rem',
                borderBottom: '2px solid #e2e8f0'
              }}>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                  {getWorkflowTitle(workflow.key)}
                </h3>
                <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                  {Object.keys(workflow.subWorkflows).length} sub-workflows •{' '}
                  {Object.values(workflow.subWorkflows).reduce((sum, sw) => sum + sw.steps.length, 0)} steps
                </div>
              </div>

              {/* Sub-Workflows */}
              {Object.values(workflow.subWorkflows).map(subWorkflow => (
                <div key={subWorkflow.key} style={{
                  borderBottom: '1px solid #e2e8f0',
                  padding: '1rem'
                }}>
                  <div style={{
                    fontSize: '1rem',
                    fontWeight: 600,
                    marginBottom: '0.75rem',
                    color: '#475569'
                  }}>
                    {getSubWorkflowTitle(subWorkflow.key)} ({subWorkflow.steps.length} steps)
                  </div>

                  {/* Steps */}
                  <div style={{ paddingLeft: '1rem' }}>
                    {subWorkflow.steps.map((step, idx) => {
                      const mvvStatus = getMvvStatus(step);
                      return (
                        <div key={idx} style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '1rem',
                          padding: '0.5rem',
                          background: idx % 2 === 0 ? '#f8fafc' : 'white',
                          borderRadius: '4px',
                          marginBottom: '0.25rem'
                        }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '0.875rem', fontWeight: 500 }}>
                              {step.display_order !== null ? `${step.display_order}. ` : ''}
                              {step.title}
                            </div>
                            {step.is_foundation_template && (
                              <div style={{
                                display: 'inline-block',
                                fontSize: '0.75rem',
                                background: '#ddd6fe',
                                color: '#6b21a8',
                                padding: '0.125rem 0.5rem',
                                borderRadius: '4px',
                                marginTop: '0.25rem'
                              }}>
                                🔒 PREREQUISITE: {step.unlock_description}
                              </div>
                            )}
                          </div>

                          <div style={{
                            fontSize: '0.75rem',
                            color: mvvStatus.color,
                            fontWeight: 500,
                            whiteSpace: 'nowrap'
                          }}>
                            {mvvStatus.label}
                          </div>

                          {(step.mvv_time_required || step.optimal_time_required) && (
                            <div style={{
                              fontSize: '0.75rem',
                              color: '#64748b',
                              whiteSpace: 'nowrap'
                            }}>
                              {step.mvv_time_required && `${step.mvv_time_required}m`}
                              {step.mvv_time_required && step.optimal_time_required && ' → '}
                              {step.optimal_time_required && `${step.optimal_time_required}m`}
                            </div>
                          )}

                          {step.action_label && (
                            <div style={{
                              fontSize: '0.75rem',
                              background: '#e0f2f1',
                              color: '#00695c',
                              padding: '0.125rem 0.5rem',
                              borderRadius: '4px',
                              whiteSpace: 'nowrap'
                            }}>
                              🔗 {step.action_label}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* MVV Audit Tab */}
      {activeTab === 'mvv' && (
        <div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: '1rem' }}>
            MVV Data Completeness Audit
          </h2>

          {/* Summary Table */}
          <div style={{
            border: '2px solid #e2e8f0',
            borderRadius: '8px',
            overflow: 'hidden',
            marginBottom: '2rem'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>Workflow</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid #e2e8f0' }}>Total</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid #e2e8f0' }}>✅ Complete</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid #e2e8f0' }}>⚠️ Partial</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid #e2e8f0' }}>❌ Missing</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid #e2e8f0' }}>% Complete</th>
                </tr>
              </thead>
              <tbody>
                {mvvAudit.map(audit => (
                  <tr key={audit.workflow_key}>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid #e2e8f0' }}>
                      {getWorkflowTitle(audit.workflow_key)}
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>
                      {audit.total}
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #e2e8f0', color: '#10b981' }}>
                      {audit.complete}
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #e2e8f0', color: '#f59e0b' }}>
                      {audit.partial}
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #e2e8f0', color: '#ef4444' }}>
                      {audit.missing}
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #e2e8f0', fontWeight: 600 }}>
                      {Math.round((audit.complete / audit.total) * 100)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Missing MVV List */}
          <h3 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '1rem' }}>
            Tasks Missing MVV Data ({missingMvv.length})
          </h3>
          <div style={{
            border: '2px solid #e2e8f0',
            borderRadius: '8px',
            padding: '1rem'
          }}>
            {missingMvv.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#10b981', padding: '1rem' }}>
                ✅ All non-binary tasks have MVV data!
              </div>
            ) : (
              missingMvv.map((task, idx) => (
                <div key={idx} style={{
                  padding: '0.75rem',
                  borderBottom: idx < missingMvv.length - 1 ? '1px solid #e2e8f0' : 'none'
                }}>
                  <div style={{ fontWeight: 500, marginBottom: '0.25rem' }}>
                    {task.title}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                    {getWorkflowTitle(task.workflow_key)} → {getSubWorkflowTitle(task.sub_workflow_key)}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Prerequisites Tab */}
      {activeTab === 'prerequisites' && (
        <div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: '1rem' }}>
            Prerequisite Tasks ({prerequisites.length})
          </h2>
          <div style={{
            border: '2px solid #e2e8f0',
            borderRadius: '8px',
            overflow: 'hidden'
          }}>
            {prerequisites.map((prereq, idx) => (
              <div key={idx} style={{
                padding: '1rem',
                borderBottom: idx < prerequisites.length - 1 ? '1px solid #e2e8f0' : 'none'
              }}>
                <div style={{
                  display: 'inline-block',
                  background: '#ddd6fe',
                  color: '#6b21a8',
                  padding: '0.25rem 0.75rem',
                  borderRadius: '4px',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  marginBottom: '0.5rem'
                }}>
                  🔒 PREREQUISITE
                </div>
                <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>
                  {prereq.title}
                </div>
                <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.5rem' }}>
                  {getWorkflowTitle(prereq.workflow_key)} → {getSubWorkflowTitle(prereq.sub_workflow_key)}
                </div>
                {prereq.unlock_description && (
                  <div style={{
                    background: '#f0fdf4',
                    border: '1px solid #bbf7d0',
                    borderRadius: '4px',
                    padding: '0.5rem',
                    fontSize: '0.875rem',
                    color: '#166534'
                  }}>
                    🔓 Unlocks: {prereq.unlock_description}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Dates Tab */}
      {activeTab === 'dates' && (
        <div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: '1rem' }}>
            Date Calculation Configuration
          </h2>
          <div style={{
            border: '2px solid #e2e8f0',
            borderRadius: '8px',
            overflow: 'hidden'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>Task</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid #e2e8f0' }}>Anchor</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid #e2e8f0' }}>Offset (days)</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid #e2e8f0' }}>Timing</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid #e2e8f0' }}>College-Specific</th>
                </tr>
              </thead>
              <tbody>
                {workflows.flatMap(wf =>
                  Object.values(wf.subWorkflows).flatMap(sw =>
                    sw.steps.map((step, idx) => (
                      <tr key={`${wf.key}-${sw.key}-${idx}`}>
                        <td style={{ padding: '0.75rem', borderBottom: '1px solid #e2e8f0' }}>
                          <div style={{ fontSize: '0.875rem', fontWeight: 500 }}>{step.title}</div>
                          <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                            {getWorkflowTitle(wf.key)}
                          </div>
                        </td>
                        <td style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>
                          <code style={{ fontSize: '0.75rem', background: '#f1f5f9', padding: '0.125rem 0.5rem', borderRadius: '4px' }}>
                            {step.anchor_id || 'none'}
                          </code>
                        </td>
                        <td style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>
                          {step.offset_days !== null ? step.offset_days : '-'}
                        </td>
                        <td style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>
                          <span style={{
                            fontSize: '0.75rem',
                            background: step.timing_sensitivity === 'high' ? '#fee2e2' : '#e0f2f1',
                            color: step.timing_sensitivity === 'high' ? '#991b1b' : '#00695c',
                            padding: '0.125rem 0.5rem',
                            borderRadius: '4px'
                          }}>
                            {step.timing_sensitivity || '-'}
                          </span>
                        </td>
                        <td style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>
                          {step.is_college_specific ? '✅' : '❌'}
                        </td>
                      </tr>
                    ))
                  )
                ).slice(0, 50)}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Intensity Levels Tab */}
      {activeTab === 'intensity' && (
        <div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: '1rem' }}>
            Timeline Pressure / Intensity Level Distribution
          </h2>
          <p style={{ color: '#64748b', marginBottom: '1.5rem' }}>
            Shows which tasks generate at each timeline pressure setting (crisis/crunch/steady).
          </p>
          <div style={{
            border: '2px solid #e2e8f0',
            borderRadius: '8px',
            overflow: 'hidden'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>Intensity Level</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid #e2e8f0' }}>Template Count</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid #e2e8f0' }}>Percentage</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>Description</th>
                </tr>
              </thead>
              <tbody>
                {intensityDistribution
                  .sort((a, b) => {
                    const order = { crisis: 1, standard: 2, comprehensive: 3, none: 4 };
                    return (order[a.intensity_level] || 99) - (order[b.intensity_level] || 99);
                  })
                  .map(item => (
                    <tr key={item.intensity_level}>
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid #e2e8f0' }}>
                        <span style={{
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          background: item.intensity_level === 'crisis' ? '#fee2e2' :
                                     item.intensity_level === 'standard' ? '#fef3c7' :
                                     item.intensity_level === 'comprehensive' ? '#dbeafe' : '#f1f5f9',
                          color: item.intensity_level === 'crisis' ? '#991b1b' :
                                 item.intensity_level === 'standard' ? '#92400e' :
                                 item.intensity_level === 'comprehensive' ? '#1e3a8a' : '#475569',
                          padding: '0.25rem 0.75rem',
                          borderRadius: '4px'
                        }}>
                          {item.intensity_level || 'none'}
                        </span>
                      </td>
                      <td style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>
                        {item.count}
                      </td>
                      <td style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #e2e8f0', fontWeight: 600 }}>
                        {item.percentage}%
                      </td>
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid #e2e8f0', fontSize: '0.875rem', color: '#64748b' }}>
                        {item.intensity_level === 'crisis' && 'Always generated (crisis/crunch/steady modes)'}
                        {item.intensity_level === 'standard' && 'Generated in crunch & steady modes only'}
                        {item.intensity_level === 'comprehensive' && 'Generated in steady mode only'}
                        {!item.intensity_level && 'No intensity level set'}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Action Buttons Tab */}
      {activeTab === 'actions' && (
        <div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: '1rem' }}>
            Action Button Configuration Audit
          </h2>
          <p style={{ color: '#64748b', marginBottom: '1.5rem' }}>
            Shows which workflows have action buttons (action_label/action_route) configured for navigation.
          </p>
          <div style={{
            border: '2px solid #e2e8f0',
            borderRadius: '8px',
            overflow: 'hidden'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>Workflow</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid #e2e8f0' }}>Total Steps</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid #e2e8f0' }}>Has Action Label</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid #e2e8f0' }}>Has Action Route</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid #e2e8f0' }}>% With Actions</th>
                </tr>
              </thead>
              <tbody>
                {actionFieldsAudit
                  .sort((a, b) => b.pctWithActions - a.pctWithActions)
                  .map(audit => (
                    <tr key={audit.workflow_key}>
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid #e2e8f0' }}>
                        {getWorkflowTitle(audit.workflow_key)}
                      </td>
                      <td style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>
                        {audit.total}
                      </td>
                      <td style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #e2e8f0', color: '#10b981' }}>
                        {audit.hasActionLabel}
                      </td>
                      <td style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #e2e8f0', color: '#10b981' }}>
                        {audit.hasActionRoute}
                      </td>
                      <td style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>
                        <span style={{
                          fontWeight: 600,
                          color: audit.pctWithActions >= 75 ? '#10b981' :
                                 audit.pctWithActions >= 25 ? '#f59e0b' : '#ef4444'
                        }}>
                          {audit.pctWithActions}%
                        </span>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Workflow Completeness Tab */}
      {activeTab === 'completeness' && (
        <div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: '1rem' }}>
            Workflow Completeness Check
          </h2>
          <p style={{ color: '#64748b', marginBottom: '1.5rem' }}>
            Validates that all 10 expected workflows exist in the system.
          </p>
          <div style={{
            border: '2px solid #e2e8f0',
            borderRadius: '8px',
            overflow: 'hidden'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>Expected Workflow</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid #e2e8f0' }}>Status</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid #e2e8f0' }}>Step Count</th>
                </tr>
              </thead>
              <tbody>
                {workflowCompleteness.map(wf => (
                  <tr key={wf.workflow_key} style={{
                    background: wf.exists ? 'white' : '#fef2f2'
                  }}>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid #e2e8f0' }}>
                      {getWorkflowTitle(wf.workflow_key)}
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>
                      <span style={{
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        background: wf.exists ? '#dcfce7' : '#fee2e2',
                        color: wf.exists ? '#166534' : '#991b1b',
                        padding: '0.25rem 0.75rem',
                        borderRadius: '4px'
                      }}>
                        {wf.exists ? '✅ Exists' : '❌ Missing'}
                      </span>
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #e2e8f0', fontWeight: 600 }}>
                      {wf.stepCount}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Summary */}
          <div style={{
            marginTop: '2rem',
            padding: '1.5rem',
            background: workflowCompleteness.every(wf => wf.exists) ? '#f0fdf4' : '#fef2f2',
            border: `2px solid ${workflowCompleteness.every(wf => wf.exists) ? '#bbf7d0' : '#fecaca'}`,
            borderRadius: '8px'
          }}>
            <div style={{
              fontSize: '1.125rem',
              fontWeight: 600,
              color: workflowCompleteness.every(wf => wf.exists) ? '#166534' : '#991b1b',
              marginBottom: '0.5rem'
            }}>
              {workflowCompleteness.every(wf => wf.exists)
                ? '✅ All expected workflows are configured!'
                : '⚠️ Some workflows are missing'}
            </div>
            <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
              {workflowCompleteness.filter(wf => wf.exists).length} of {workflowCompleteness.length} workflows exist
            </div>
          </div>
        </div>
      )}

      {/* Refresh Button */}
      <div style={{ marginTop: '2rem', textAlign: 'center' }}>
        <button
          onClick={loadVerificationData}
          style={{
            background: '#26a69a',
            color: 'white',
            border: '2px solid #0f172a',
            borderRadius: '6px',
            padding: '0.75rem 1.5rem',
            fontSize: '0.875rem',
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          🔄 Refresh Data
        </button>
      </div>
    </div>
  );
};

export default WorkflowVerificationPanel;
